
import { useState, useEffect } from 'react';
import { Menu, X, Home, Users, LogIn, Shield } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import UserMenu from './UserMenu';
import { useAuth } from '@/hooks/useAuth';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Check if user is admin - only use database field
  const isAdmin = user?.isAdmin;

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const handleNavigation = (path: string) => {
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
    navigate(path);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 px-4 sm:px-6 md:px-10 py-3 sm:py-4 ${
        isScrolled ? 'bg-emu-blue shadow-md' : 'bg-emu-blue'
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center space-x-3 sm:space-x-6">
          <Link to="/" className="flex items-center">
            <img 
              src="/lovable-uploads/3a713958-1f15-411b-94c6-9acd4bd6302a.png" 
              alt="EMU Mentoring" 
              className="h-8 xs:h-10 sm:h-12 md:h-14"
              onError={(e) => {
                e.currentTarget.src = '/emu-logo-white.png';
              }}
            />
          </Link>
          
          {/* Admin button - only show for admin users */}
          {isAdmin && (
            <Link 
              to="/admin" 
              className="hidden md:flex items-center text-white nav-link-hover font-medium space-x-2"
            >
              <Shield size={18} strokeWidth={2.5} />
              <span>Admin</span>
            </Link>
          )}
        </div>

        <nav className="hidden md:flex items-center">
          <div className="flex items-center space-x-8">
            <Link 
              to="/" 
              className="flex items-center text-white nav-link-hover font-medium space-x-2"
            >
              <Home size={20} strokeWidth={2.5} />
              <span>Home</span>
            </Link>
            <Link 
              to="/search-mentors" 
              className="flex items-center text-white nav-link-hover font-medium space-x-2"
            >
              <Users size={20} strokeWidth={2.5} />
              <span>Our Mentors</span>
            </Link>
            <Link 
              to="/about" 
              className="text-white nav-link-hover font-medium"
            >
              About
            </Link>
            <Link 
              to="/contact" 
              className="text-white nav-link-hover font-medium"
            >
              Contact Us
            </Link>
            {user && (
              <Link 
                to="/dashboard"
                className="text-white nav-link-hover font-medium"
              >
                Dashboard
              </Link>
            )}
          </div>
          {user ? (
            <div className="flex items-center ml-8">
              <UserMenu />
            </div>
          ) : (
            <Button 
              variant="outline"
              onClick={() => navigate('/login')}
              className="ml-8 bg-white hover:bg-gray-100 text-emu-blue"
            >
              <LogIn className="mr-2 h-4 w-4" />
              Log in
            </Button>
          )}
        </nav>

        <button 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden text-white focus:outline-none p-2 rounded-lg hover:bg-white/10 transition-colors"
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <div 
        className={`md:hidden fixed inset-0 bg-emu-blue z-40 transform transition-transform duration-300 ease-in-out ${
          mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        } pt-20 sm:pt-24 px-6 overflow-y-auto`}
      >
        <nav className="flex flex-col space-y-8 pb-8">
          {/* Admin button in mobile menu */}
          {isAdmin && (
            <button 
              onClick={() => handleNavigation("/admin")}
              className="flex items-center text-white hover:text-gray-200 text-xl font-medium transition-colors duration-300"
            >
              <Shield size={20} className="mr-2" />
              <span>Admin</span>
            </button>
          )}
          
          <button 
            onClick={() => handleNavigation("/")}
            className="flex items-center text-white hover:text-gray-200 text-lg xs:text-xl font-medium transition-colors duration-300 py-3 px-2 rounded-lg hover:bg-white/10 w-full justify-start"
          >
            <Home size={24} className="mr-4" />
            <span>Home</span>
          </button>
          <button 
            onClick={() => handleNavigation("/search-mentors")}
            className="flex items-center text-white hover:text-gray-200 text-lg xs:text-xl font-medium transition-colors duration-300 py-3 px-2 rounded-lg hover:bg-white/10 w-full justify-start"
          >
            <Users size={24} className="mr-4" />
            <span>Our Mentors</span>
          </button>
          <button 
            onClick={() => handleNavigation("/about")}
            className="text-white hover:text-gray-200 text-lg xs:text-xl font-medium transition-colors duration-300 py-3 px-2 rounded-lg hover:bg-white/10 w-full text-left"
          >
            About
          </button>
          <button 
            onClick={() => handleNavigation("/contact")}
            className="text-white hover:text-gray-200 text-lg xs:text-xl font-medium transition-colors duration-300 py-3 px-2 rounded-lg hover:bg-white/10 w-full text-left"
          >
            Contact Us
          </button>
          {user ? (
            <>
              <button 
                onClick={() => handleNavigation("/dashboard")}
                className="text-white hover:text-gray-200 text-xl font-medium transition-colors duration-300"
              >
                Dashboard
              </button>
              <div className="mt-4 pt-4 border-t border-white/20">
                <UserMenu />
              </div>
            </>
          ) : (
            <Button 
              variant="outline"
              onClick={() => navigate('/login')}
              className="w-full bg-white hover:bg-gray-100 text-emu-blue"
            >
              <LogIn className="mr-2 h-4 w-4" />
              Log in
            </Button>
          )}
        </nav>
      </div>
    </header>
  );
};

export default Navbar;
